import { Component, Input } from '@angular/core';
import { TsPieceComponent } from '../ts-piece/ts-piece.component';

/**
 * Hueco para una captura real del producto. Con `src` muestra la imagen. Sin
 * imagen:
 *  - si se pasa `brandColor`, muestra un panel de marca (pieza del logo en ese
 *    color sobre un tinte suave) → válido para producción, no "pendiente";
 *  - si no, muestra el marcador de desarrollo con la pista de qué captura va
 *    aquí (equivale al `image-slot.js` del prototipo).
 */
@Component({
  selector: 'app-image-slot',
  standalone: true,
  imports: [TsPieceComponent],
  template: `
    @if (src && !failed) {
      <img
        class="image-slot__img"
        [src]="src"
        [alt]="alt || hint"
        [style.min-height.px]="minHeight"
        loading="lazy"
        (error)="failed = true" />
    } @else if (brandColor) {
      <div
        class="image-slot__brand"
        role="img"
        [attr.aria-label]="alt || hint"
        [style.min-height.px]="minHeight"
        [style.--slot-accent]="brandColor">
        <app-ts-piece
          class="image-slot__brand-piece"
          piece="full"
          [color]="brandColor"
          [size]="80" />
      </div>
    } @else {
      <div
        class="image-slot__ph"
        role="img"
        [attr.aria-label]="hint"
        [style.min-height.px]="minHeight">
        <i class="pi pi-image"></i>
        <span>{{ hint }}</span>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .image-slot__img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        /* Los pantallazos se recortan por arriba (cabecera de la app), no por el centro. */
        object-position: top;
        border-radius: 10px;
      }
      .image-slot__ph,
      .image-slot__brand {
        width: 100%;
        height: 100%;
        min-height: 160px;
        box-sizing: border-box;
        border: 1px solid var(--ts-card-bd);
        border-radius: 10px;
      }
      .image-slot__ph {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 20px;
        text-align: center;
        background: var(--ts-bg-alt);
        color: var(--ts-text-muted);
      }
      .image-slot__ph i {
        font-size: 26px;
        opacity: 0.7;
      }
      .image-slot__ph span {
        font-size: 13px;
        line-height: 1.4;
        max-width: 240px;
      }
      .image-slot__brand {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        background: var(--ts-bg-alt);
      }
      /* Tinte suave del color del sector (sin depender de color-mix). */
      .image-slot__brand::before {
        content: '';
        position: absolute;
        inset: 0;
        background: var(--slot-accent, var(--ts-primary));
        opacity: 0.1;
      }
      .image-slot__brand-piece {
        position: relative;
        opacity: 0.9;
        filter: drop-shadow(0 6px 16px rgba(8, 18, 22, 0.12));
      }
    `,
  ],
})
export class ImageSlotComponent {
  /** Pista de la captura pendiente (marcador de desarrollo). */
  @Input() hint = 'Captura real del producto';
  /** Ruta de la imagen real; si se rellena, se muestra en vez del hueco. */
  @Input() src?: string;
  /** Texto alternativo (por defecto, la pista). */
  @Input() alt?: string;
  /** Alto mínimo del hueco/imagen en px (proporción del diseño). */
  @Input() minHeight?: number;
  /** Color de sector para el panel de marca cuando no hay imagen. */
  @Input() brandColor?: string;

  /** La imagen falló al cargar (aún no existe) → se muestra el fallback. */
  failed = false;
}
